/**
 * @fileoverview `useLoginTranslate(t)` — the login corpus's translator.
 * @package @puffin-core/login-shell
 *
 * ## The bug this closes (AAS-I18N-002 W2, ruling 2 / Q-QF4-5)
 *
 * Every login-shell component resolved its copy through
 * `resolveString(t, key)`, whose documented fallback is the shipped ENGLISH
 * `DEFAULT_LOGIN_STRINGS`. The `t` prop was optional, and a repo-wide census of the
 * 12 `<LoginShell>` render sites across 11 files found it passed at **zero** of
 * them. So the first screen every user sees rendered English in every locale, in
 * all ten apps — under an `<html lang="es">` that Q-QF4-3 had just made correct,
 * which turns the mismatch into a WCAG SC 3.1.1 failure in the other direction.
 *
 * `web/no-hardcoded-strings` scores this **0** in every app and always will: the
 * literals live in a package, and the gate censuses `apps/**`. RECIPE miss-class 9.
 *
 * ## Resolution order (unchanged at the top, extended at the bottom)
 *
 *   1. the consumer's `t` prop — still honoured first, so the published
 *      component contract does not change for any caller that supplies one;
 *   2. the `login` i18next namespace on the instance in the React tree —
 *      registered by `loadLoginStrings`, wired through `createAppI18n`;
 *   3. `DEFAULT_LOGIN_STRINGS` — the English degrade path, byte-identical to
 *      today's behaviour for an unwired tree.
 *
 * Steps 1 and 3 are `resolveString`'s existing contract; this hook only inserts
 * step 2 between them. An app that never calls `loadLoginStrings` renders exactly
 * what it rendered before — no crash, no raw dotted key.
 *
 * ## Why this hook interpolates itself
 *
 * The bundles use SINGLE-curly `{email}` placeholders (see `strings/es.js`), so
 * i18next's `{{…}}` interpolator never touches them and this hook expands them via
 * the package's own `interpolate()`. That makes placeholder expansion identical on
 * the wired and the unwired path. RECIPE §6 records the alternative failing in
 * production: react-i18next's no-instance `t` returns a `defaultValue` **without
 * interpolating it**, and page-shell shipped a literal `{{label}}` into an
 * `aria-label` because of it.
 *
 * ⚠️ Hook-placement rule (RECIPE §3): call this **immediately above the first
 * computed hook that consumes the translator**. Higher and
 * `web/component-state-order` reports every translator-free `useCallback` beneath
 * it; lower and you ship a TDZ crash that lint scores 0 on.
 */

import { createInterpolator } from '@puffin-core/i18n-bootstrap/strings-kit';
import { createNamespaceTranslate } from '@puffin-core/i18n-bootstrap/namespace-translate';

import { LOGIN_NAMESPACE } from '../strings/namespace.js';
import { resolveString } from '../strings/resolveString.js';

/**
 * Build the login corpus translator for a component.
 *
 * I18N-RENDER-002 Phase 0 (2026-08-20): bound from the ONE shared factory.
 *
 * This hook was the third of three drifted shapes — a 2-param `chained`, a 3-arg `resolveString`
 * call, and interpolation gated on `params &&`. All three collapse into the canonical shape without
 * behaviour change: the `params &&` guard existed only to stop a `keep`-policy interpolator running
 * with no params, and a `keep` interpolator with no params is already a no-op, because every
 * placeholder is left intact. Threading `params` through `chained` versus closing over it is the
 * same call in both arms.
 *
 * @param {((key: string, params?: object) => string|undefined|null)} [consumerT]
 *   The optional `t` prop a caller may pass. Kept first in the resolution order so
 *   the existing component contract is unchanged.
 * @returns {(key: string, params?: object) => string|undefined} A translator that
 *   resolves consumer → `login` namespace → English default, then expands any
 *   `{param}` placeholders.
 */
export const useLoginTranslate = createNamespaceTranslate({
  namespace: LOGIN_NAMESPACE,
  resolveString,
  interpolate: createInterpolator({ braces: 'single', onMissing: 'keep' }),
});

export default useLoginTranslate;
